import { Anchor, Box, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { TocEntry } from "@mountayaapp/markdoc";

import classes from "./docsToc.module.css";

/**
 * DocsTocProps carry the page's table-of-contents entries (its level-2 and
 * level-3 headings).
 */
interface DocsTocProps {
  entries: TocEntry[];
}

/**
 * useActiveHeading tracks which heading is the one currently being read. It
 * observes every heading element and narrows the intersection band to the top
 * of the viewport (the bottom 70% is excluded), so the active id is the topmost
 * heading scrolled into that band — the heading the reader is sitting under.
 */
const useActiveHeading = (entries: TocEntry[]): string | null => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (entries.length === 0) {
      return;
    }

    const elements = entries
      .map((entry) => document.getElementById(entry.id))
      .filter((element): element is HTMLElement => element !== null);

    if (elements.length === 0) {
      return;
    }

    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          if (record.isIntersecting) {
            visible.add(record.target.id);
          } else {
            visible.delete(record.target.id);
          }
        }

        // The active heading is the first entry, in document order, currently
        // inside the top band; anything else stays as it was so the highlight
        // never blanks out between bands.
        const firstVisible = entries.find((entry) => visible.has(entry.id));

        if (firstVisible) {
          setActiveId(firstVisible.id);
        }
      },
      { rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );

    for (const element of elements) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [entries]);

  return activeId;
};

/**
 * DocsToc renders the in-page table of contents as anchor links to each
 * heading's id. Level-3 entries are indented under their level-2 parents so the
 * outline reads at a glance; the links are plain in-page anchors that the
 * browser scrolls to, matching the slug ids the headings render with. The
 * heading the reader is currently under reads in the default text color, bolded,
 * and marked on the rule down the left of the list, so the outline doubles as a
 * position indicator.
 */
export const DocsToc = ({ entries }: DocsTocProps) => {
  const { t } = useTranslation("docs");
  const activeId = useActiveHeading(entries);

  if (entries.length === 0) {
    return null;
  }

  return (
    // The landmark takes the name of the heading it already shows, so the contents rail
    // is told apart from the page rail and the tab row rather than being a third
    // anonymous "navigation" in the list a screen reader offers.
    <Box aria-label={t("toc.onThisPage")} component="nav">
      <Text c="dimmed" fw={600} mb="sm" size="xs" tt="uppercase">
        {t("toc.onThisPage")}
      </Text>
      <Stack gap="sm">
        {entries.map((entry) => {
          const isActive = entry.id === activeId;

          return (
            <Anchor
              c={isActive ? undefined : "dimmed"}
              className={classes.entry}
              data-active={isActive ? true : undefined}
              data-nested={entry.level === 3 ? true : undefined}
              fw={isActive ? 700 : undefined}
              href={`#${entry.id}`}
              key={entry.id}
              size="sm"
            >
              {entry.title}
            </Anchor>
          );
        })}
      </Stack>
    </Box>
  );
};
